import { observer } from 'mobx-react-lite'

import { Form, FormField } from '@/Core/UI/Form'
import {
    EntityBody,
    EntityHeader,
    EntityHero,
    EntityLayout,
    FieldCell,
    FieldGrid,
    FieldSection,
    HeaderChip,
    SectionEmpty,
} from '@/Ship/Components/EntityCard'
import { Hint } from '@/Ship/Components/Hint'

import styles from './DepartmentFormTemplate.module.css'

import type { DepartmentCardPerson, DepartmentCardValues } from '../Contracts/IDepartment'

interface DepartmentFormContext {
    isEditable?: boolean
    values?: DepartmentCardValues
}

const personName = (person: DepartmentCardPerson): string =>
    person.fullName ?? person.shortName ?? '—'

/**
 * Шапка картки — окремим observer-компонентом: вона читає назву, яку
 * редагують, а ядровий `FormField` на кожне перемальовування шаблону
 * перемонтовує всі поля форми (`SPEC-002` §4.2).
 */
const DepartmentCardHeader = observer(({ formContext }: { formContext: DepartmentFormContext }) => {
    const values = formContext.values ?? {}
    const title = (values.title ?? '').trim()

    if (formContext.isEditable === true || title === '') return null

    return (
        <EntityHero>
            <EntityHeader
                title={title}
                {...(values.isMain ? { badges: <HeaderChip tone="warning">Головний</HeaderChip> } : {})}
                {...(values.parent?.title ? { subtitle: `Підрозділ: ${values.parent.title}` } : {})}
            />
        </EntityHero>
    )
})

/**
 * Склад відділу — лише перегляд: співробітника до відділу додають у його
 * картці (Р-13). Деактивовані не показуються, як і в дереві (ВП-4).
 */
const DepartmentPeople = observer(({ formContext }: { formContext: DepartmentFormContext }) => {
    const people = (formContext.values?.users ?? []).filter((person) => (person.status ?? 'active') === 'active')

    return (
        <FieldSection title="Співробітники" extra={people.length > 0 ? people.length : undefined}>
            <Hint id="team.departments.employees">
                Працівників до відділу додає супер адміністратор у картці співробітника, полем «Відділи компанії».
                Одна людина може працювати в кількох відділах.
            </Hint>
            {people.length === 0 ? (
                <SectionEmpty>У відділі ще немає працівників.</SectionEmpty>
            ) : (
                <div className={styles['list']}>
                    {people.map((person) => (
                        <div key={person.uuid ?? person['@id']} className={styles['person']}>
                            <span>{personName(person)}</span>
                            {person.position && <span className={styles['position']}>{person.position}</span>}
                        </div>
                    ))}
                </div>
            )}
        </FieldSection>
    )
})

/**
 * Картка «Підрозділ» (`SPEC-006` §7.4) на спільних правилах карток
 * (`EntityLayout`): шапка з назвою, блок «Головне» — назва, підрозділ,
 * керівник; блок «Співробітники» — склад відділу (ПІБ і посада).
 *
 * Поле «Підрозділ» поводиться за режимом (`parentDepFn` ресурсу): з вузла
 * дерева воно заповнене й вимкнене, з таблиці — обов'язкове, у редагуванні —
 * лише читання, у головного — сховане.
 *
 * ⚠️ Значень полів, які редагують, шаблон не читає — лише через окремі
 * observer-компоненти (`SPEC-002` §4.2): інакше ядровий `FormField`
 * перемонтовував би всі поля на кожну літеру.
 */
export const DepartmentFormTemplate = ({ formContext }: { formContext: DepartmentFormContext }) => {
    const uuid = formContext.values?.uuid
    const isNew = typeof uuid !== 'string' || uuid === ''

    return (
        <EntityLayout>
            <DepartmentCardHeader formContext={formContext} />
            <EntityBody
                main={
                    <>
                        <FieldSection title="Головне">
                            <Form formContext={formContext}>
                                <FieldGrid>
                                    <FieldCell wide>
                                        <FormField name="title" />
                                    </FieldCell>
                                    <FieldCell>
                                        <FormField name="parent" />
                                    </FieldCell>
                                    <FieldCell>
                                        <FormField name="chief" />
                                    </FieldCell>
                                </FieldGrid>
                            </Form>
                        </FieldSection>
                        {isNew ? null : <DepartmentPeople formContext={formContext} />}
                    </>
                }
            />
        </EntityLayout>
    )
}
